import { Loader, SearchableSelect } from 'components';
import { useSelectedLocation } from 'contexts';
import { useCarrierAPI } from 'hooks';
import {
  createContext,
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTruckingCompanyById } from 'services';
import { CarrierContact, TruckingCompany } from 'types';
import { mapById } from 'utils';

import { NOTES_MAX_LENGTH, USDOT_MAX_LENGTH, USDOT_PATTERN } from '../carrier-form.constants';

type EditCarrierContextType = {
  carrier: TruckingCompany | null;
  formRef: RefObject<HTMLFormElement>;
  setCarrier: Dispatch<SetStateAction<TruckingCompany | null>>;
  setContacts: (contact?: CarrierContact, index?: number) => void;
  handleReset: FormEventHandler<HTMLFormElement>;
  handleSubmit: FormEventHandler<HTMLFormElement>;
};

const EditCarrierContext = createContext<EditCarrierContextType>({
  carrier: null,
  formRef: { current: null },
  setCarrier: () => {},
  setContacts: () => {},
  handleReset: () => {},
  handleSubmit: () => {}
});

const useEditCarrier = () => useContext(EditCarrierContext);

const Contact: FC<{ contact: CarrierContact; index: number }> = ({ contact, index }) => {
  const { carrier, setContacts } = useEditCarrier();
  const { allLocations } = useSelectedLocation();

  const locationMap = useMemo(() => mapById(allLocations ?? []), [allLocations]);

  const handleChange = useCallback(
    (partial: Partial<CarrierContact>) => {
      setContacts({ ...contact, ...partial }, index);
    },
    [contact, index, setContacts]
  );

  const handleDelete = useCallback(() => {
    setContacts(undefined, index);
  }, [index, setContacts]);

  return (
    <>
      {!!carrier && (
        <div className="grid-4 p-3 mb-4">
          <div>
            <h6 className="text-info-dark">Location</h6>
            <div className="mt-2 w-100">
              <SearchableSelect
                onValueSelected={id => {
                  handleChange({ location: locationMap.get(Number(id)) });
                }}
                options={[...locationMap.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))}
                value={`${contact.location?.id ?? ''}`}
                required
                data-cy={`contact-${index}-location-input`}
              />
            </div>
          </div>
          <div>
            <h6 className="text-info-dark">Email(s)</h6>
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ email: e.target.value })}
              value={contact.email ?? ''}
              required
              data-cy={`contact-${index}-email-input`}
            />
          </div>
          <div className="flex-fill d-flex align-items-end h-100">
            <button
              className="btn-back p-2"
              type="button"
              onClick={handleDelete}
              disabled={carrier.contacts?.length === 0}
              data-cy={`contact-${index}-delete-button`}
            >
              <span className="material-icons icon-xlarge">delete</span>
            </button>
          </div>
          <div className="border-bottom"></div>
        </div>
      )}
    </>
  );
};

export const EditCarrier: FC = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { updateCarrier } = useCarrierAPI();

  const [carrier, setCarrier] = useState<TruckingCompany | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!!params.id && !isNaN(Number(params.id))) {
      getTruckingCompanyById(Number(params.id)).then(setCarrier);
    }
  }, [params.id]);

  const setContacts = (contact?: CarrierContact, index?: number) => {
    setCarrier(previous => {
      if (!!previous) {
        const contacts = Object.assign([], previous.contacts);
        if (!contact) {
          contacts.splice(index ?? contacts.length, 1);
        } else {
          contacts.splice(index ?? contacts.length, 1, contact);
        }
        return { ...previous, contacts };
      }

      return previous;
    });
  };

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    if (!!params.id) {
      navigate(`/scac-management/carriers/${params.id}`, { replace: true });
      return;
    }
    navigate('/scac-management', { replace: true });
  }, [navigate, params.id]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!carrier || !carrier.scacCode || !carrier.name) {
        setIsSubmitting(false);
        return;
      }

      if ((carrier.notes?.length ?? 0) > NOTES_MAX_LENGTH) {
        setIsSubmitting(false);
        return;
      }

      if (carrier.usdotNumber && !new RegExp(`^${USDOT_PATTERN}$`).test(carrier.usdotNumber)) {
        setIsSubmitting(false);
        return;
      }

      const response = await updateCarrier({
        id: carrier.id,
        name: carrier.name,
        scacCode: carrier.scacCode,
        email: carrier.email,
        deleted: carrier.deleted,
        contacts: carrier.contacts ?? [],
        notes: carrier.notes,
        usdotNumber: carrier.usdotNumber
      });

      setIsSubmitting(false);
      navigate(`/scac-management/carriers/${response ? response.id : carrier.id}`, { replace: true });
    },
    [carrier, updateCarrier, navigate]
  );

  return (
    <EditCarrierContext.Provider
      value={{
        carrier,
        formRef,
        setCarrier,
        setContacts,
        handleReset,
        handleSubmit
      }}
    >
      <Loader loading={!carrier}>
        {!!carrier && (
          <form
            className="page"
            id="edit-carrier"
            onReset={handleReset}
            onSubmit={handleSubmit}
            ref={formRef}
            data-cy="edit-carrier-form"
          >
            <div className="page-header">
              <h1>CARRIER {carrier.scacCode}</h1>
              <div className="d-flex g-3 flex-row-reverse">
                <Loader loading={isSubmitting}>
                  <button className="btn-primary" type="submit">
                    SUBMIT
                  </button>
                </Loader>
                <button className="btn-outline" type="reset">
                  CANCEL
                </button>
              </div>
            </div>
            <div className="page-content flex-fill">
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">Carrier Information</h4>
                </div>
                <div className="card-body">
                  <div className="grid-4 p-2">
                    <div>
                      <h6 className="text-info-dark">Name</h6>
                      <input
                        type="text"
                        className="mt-2 w-100"
                        defaultValue={carrier.name ?? ''}
                        onChange={e => setCarrier(c => c && { ...c, name: e.target.value })}
                        required
                        data-cy="edit-carrier-name-input"
                      />
                    </div>
                    <div className="position-relative">
                      <h6 className="text-info-dark">SCAC Code</h6>
                      <p className="text-black">{carrier.scacCode}</p>
                    </div>
                    <div>
                      <h6 className="text-info-dark">USDOT Number</h6>
                      <input
                        type="text"
                        className="mt-2 w-100"
                        value={carrier.usdotNumber ?? ''}
                        onChange={e => setCarrier(c => c && { ...c, usdotNumber: e.target.value })}
                        pattern={USDOT_PATTERN}
                        maxLength={USDOT_MAX_LENGTH}
                        title="USDOT must be 8–10 alphanumeric characters"
                        data-cy="edit-carrier-usdot-number-input"
                      />
                    </div>
                  </div>
                  <div className="grid-2 p-2">
                    <div>
                      <h6 className="text-info-dark">Notes</h6>
                      <textarea
                        className="mt-2 w-100"
                        rows={3}
                        maxLength={NOTES_MAX_LENGTH}
                        value={carrier.notes ?? ''}
                        onChange={e => setCarrier(c => c && { ...c, notes: e.target.value })}
                        data-cy="edit-carrier-notes-input"
                      />
                      <small
                        className={(carrier.notes?.length ?? 0) >= NOTES_MAX_LENGTH ? 'text-danger' : 'text-info-dark'}
                      >
                        {carrier.notes?.length ?? 0}/{NOTES_MAX_LENGTH}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-primary">
                <div className="card-header px-3">
                  <h4 className="text-uppercase">Contact(s)</h4>
                </div>
              </div>
              <div className="card-body">
                <div className="p-2">
                  {carrier.contacts?.map((contact, index) => 
                    <Contact contact={contact} index={index} key={`contact-${index}`} />
                  )}
                  <div className="p-2">
                    <button
                      className="btn-outline"
                      type="button"
                      onClick={() => setContacts({} as CarrierContact)}
                      data-cy="edit-carrier-add-contact-button"
                    >
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Loader>
    </EditCarrierContext.Provider>
  );
};
